/**
 * @fileoverview Provides a class to monitor network bandwidth by downloading a probe file.
 * It classifies the connection and uses an Exponential Moving Average (EMA) to smooth out measurements.
 */

export type NetworkClassification = 'slow' | 'medium' | 'fast' | 'unknown'

export interface BandwidthMonitorOptions {
  /** The URL to the probe file on your CDN. */
  probeUrl: string
  /** The known size of the probe file in bytes. */
  probeSizeInBytes: number
  /** Smoothing factor for the EMA calculation (0 to 1). A lower value means more smoothing. */
  smoothingAlpha?: number
  /** Timeout for the measurement fetch request in milliseconds. */
  requestTimeoutMs?: number
}

const DEFAULT_SMOOTHING_ALPHA = 0.3 // Lower = more smoothing (70% historical, 30% new)
const DEFAULT_REQUEST_TIMEOUT_MS = 10000 // 10 seconds
const STORAGE_KEY = 'martini-bandwidth-data'
const MEASUREMENT_EXPIRY_MS = 12 * 60 * 60 * 1000 // 12 hours
/**
 * Delay before the first probe when no recent measurement exists. The probe
 * is a full 2 MB download; at 2 s it landed squarely on the editor boot and
 * competed with the canvas chunks for the same connection (measured at 2.1 s
 * of a 9 Mbps link). Fifteen seconds clears the boot on all but the slowest
 * links, and the idle callback below keeps it out of any busy frame after that.
 */
export const INITIAL_MEASUREMENT_DELAY_MS = 15_000

/**
 * Bandwidth classification thresholds in Mbps
 * - Slow: < 2 Mbps (suitable for 480p streaming, limited features)
 * - Medium: 2-6 Mbps (suitable for 480p with some prefetching)
 * - Fast: > 6 Mbps (full quality 720p with aggressive prefetching)
 */
const BANDWIDTH_THRESHOLD_SLOW_MBPS = 2
const BANDWIDTH_THRESHOLD_FAST_MBPS = 6

function getBrowserLocalStorage(): Storage | null {
  if (typeof window === 'undefined' || !('localStorage' in window)) return null

  try {
    return window.localStorage
  } catch {
    return null
  }
}

/**
 * Run `work` in the next idle period (next tick where the browser has no idle
 * callback). Always asynchronous. Returns a cancel function; cancelling after
 * `work` ran is a no-op.
 */
function runWhenIdle(work: () => void): () => void {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    const handle = window.requestIdleCallback(() => work(), { timeout: 5000 })
    return () => window.cancelIdleCallback(handle)
  }
  const timer = setTimeout(work, 0)
  return () => clearTimeout(timer)
}

/**
 * Monitors network bandwidth by periodically downloading a test file from the CDN.
 * Provides a smoothed bandwidth estimate and a simple classification.
 */
export class BandwidthMonitor {
  private readonly probeUrl: string
  private readonly probeSizeInBytes: number
  private readonly smoothingAlpha: number
  private readonly requestTimeoutMs: number

  private smoothedMbps: number = 0
  private isMeasuring: boolean = false
  private abortController: AbortController | null = null
  // Whatever step of the startup probe is pending — the delay timer, then the
  // idle callback — so stop() can cancel it before the download starts.
  private cancelInitialMeasurement: (() => void) | null = null
  private listeners = new Set<(classification: NetworkClassification) => void>()

  public classification: NetworkClassification = 'unknown'

  constructor(options: BandwidthMonitorOptions) {
    this.probeUrl = options.probeUrl
    this.probeSizeInBytes = options.probeSizeInBytes
    this.smoothingAlpha = options.smoothingAlpha ?? DEFAULT_SMOOTHING_ALPHA
    this.requestTimeoutMs = options.requestTimeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS

    // Restore smoothed value from localStorage if recent (within 12 hours)
    const storage = getBrowserLocalStorage()
    if (storage) {
      const stored = storage.getItem(STORAGE_KEY)
      if (stored) {
        try {
          const data = JSON.parse(stored)
          const age = Date.now() - data.timestamp

          if (age < MEASUREMENT_EXPIRY_MS && data.mbps > 0) {
            // Recent measurement - restore it
            this.smoothedMbps = data.mbps
            this.updateClassification()
            console.log(
              `[BandwidthMonitor] Restored measurement from ${(age / 1000 / 60).toFixed(0)}min ago: ${data.mbps.toFixed(2)} Mbps`,
            )
          } else {
            // Expired - clear it
            storage.removeItem(STORAGE_KEY)
            console.log('[BandwidthMonitor] Previous measurement expired, starting fresh')
          }
        } catch {
          // Invalid format - clear it
          storage.removeItem(STORAGE_KEY)
        }
      }
    }
  }

  /**
   * Subscribe to classification changes.
   * @param listener Callback function that receives the new classification
   * @returns Unsubscribe function
   */
  public subscribe(listener: (classification: NetworkClassification) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /**
   * Starts monitoring the network. Sets up listeners for network changes.
   *
   * The first probe only runs when nothing recent is known: a measurement
   * restored from the last 12 hours already classified the connection in the
   * constructor, and re-downloading 2 MB on every page open to refine it is
   * not worth the bandwidth it takes from the boot. Network-change events
   * still trigger a fresh measurement either way.
   */
  public start(): void {
    if (this.smoothedMbps > 0) {
      console.log('[BandwidthMonitor] Recent measurement restored; skipping the startup probe')
    } else {
      const timer = setTimeout(() => {
        this.cancelInitialMeasurement = runWhenIdle(() => {
          this.cancelInitialMeasurement = null
          void this.measure()
        })
      }, INITIAL_MEASUREMENT_DELAY_MS)
      this.cancelInitialMeasurement = () => clearTimeout(timer)
    }

    // Re-measure when the browser signals a network change (if supported).
    // Network Information API is not available in all browsers (notably Safari)
    if ('connection' in navigator && navigator.connection) {
      // @ts-expect-error - Network Information API not in lib.dom.d.ts
      navigator.connection.addEventListener('change', this.handleNetworkChange)
    }
  }

  /**
   * Stops monitoring and cleans up event listeners.
   */
  public stop(): void {
    if ('connection' in navigator && navigator.connection) {
      // @ts-expect-error - Network Information API not in lib.dom.d.ts
      navigator.connection.removeEventListener('change', this.handleNetworkChange)
    }
    if (this.cancelInitialMeasurement) {
      this.cancelInitialMeasurement()
      this.cancelInitialMeasurement = null
    }
    if (this.abortController) {
      this.abortController.abort()
    }
    this.listeners.clear()
  }

  /**
   * Performs a single bandwidth measurement.
   * @returns {Promise<void>} A promise that resolves when the measurement is complete.
   */
  public async measure(): Promise<void> {
    if (this.isMeasuring) {
      console.warn('[BandwidthMonitor] Measurement already in progress.')
      return
    }

    this.isMeasuring = true

    // Use request-scoped controller to prevent cross-request interference
    const controller = new AbortController()
    this.abortController = controller // Keep reference for stop() method

    // Use a unique URL for each request to prevent caching.
    const url = `${this.probeUrl}?_cb=${Date.now()}`
    const startTime = performance.now()

    const timeout = setTimeout(() => {
      controller.abort()
    }, this.requestTimeoutMs)

    try {
      const response = await fetch(url, { signal: controller.signal, cache: 'no-store' })

      if (!response.ok) {
        throw new Error(`Measurement request failed: ${response.statusText}`)
      }

      await response.arrayBuffer() // Consume the body to complete the download.

      const endTime = performance.now()
      const durationMs = endTime - startTime

      if (durationMs > 0) {
        const durationSeconds = durationMs / 1000
        const bitsLoaded = this.probeSizeInBytes * 8
        const bps = bitsLoaded / durationSeconds
        const mbps = bps / 1_000_000

        this.updateSmoothedMbps(mbps)
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('[BandwidthMonitor] Measurement timed out or was aborted. Assuming slow connection.')
        // If it times out, it's very slow. Nudge the estimate downward.
        this.updateSmoothedMbps(0.1)
      } else {
        console.error('[BandwidthMonitor] Measurement failed:', error)
      }
    } finally {
      clearTimeout(timeout) // Always cleanup timeout
      this.isMeasuring = false
      this.abortController = null
      this.updateClassification()
    }
  }

  /**
   * Updates the smoothed Mbps value using an Exponential Moving Average.
   * Persists the smoothed value to localStorage for cross-session smoothing.
   * @param newMeasurement The new bandwidth measurement in Mbps.
   */
  private updateSmoothedMbps(newMeasurement: number): void {
    if (this.smoothedMbps === 0) {
      // First measurement
      this.smoothedMbps = newMeasurement
    } else {
      this.smoothedMbps = newMeasurement * this.smoothingAlpha + this.smoothedMbps * (1 - this.smoothingAlpha)
    }
    console.log(
      `[BandwidthMonitor] New measurement: ${newMeasurement.toFixed(2)} Mbps, Smoothed value: ${this.smoothedMbps.toFixed(2)} Mbps`,
    )

    // Persist to localStorage for cross-session smoothing with timestamp
    const storage = getBrowserLocalStorage()
    if (storage) {
      storage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          mbps: this.smoothedMbps,
          timestamp: Date.now(),
        }),
      )
    }
  }

  /**
   * Updates the public classification property based on the smoothed bandwidth.
   * Notifies all subscribers if the classification changes.
   */
  private updateClassification(): void {
    const previousClassification = this.classification

    if (this.smoothedMbps === 0) {
      this.classification = 'unknown'
    } else if (this.smoothedMbps < BANDWIDTH_THRESHOLD_SLOW_MBPS) {
      this.classification = 'slow'
    } else if (
      this.smoothedMbps >= BANDWIDTH_THRESHOLD_SLOW_MBPS &&
      this.smoothedMbps <= BANDWIDTH_THRESHOLD_FAST_MBPS
    ) {
      this.classification = 'medium'
    } else {
      this.classification = 'fast'
    }

    if (previousClassification !== this.classification) {
      console.log(`[BandwidthMonitor] Classification changed: ${previousClassification} → ${this.classification}`)
      // Notify all subscribers of the change
      this.listeners.forEach(listener => listener(this.classification))
    }
  }

  /**
   * Gets the current smoothed bandwidth in Mbps
   */
  public getSmoothedMbps(): number {
    return this.smoothedMbps
  }

  // Use arrow functions for handlers to preserve `this` context.
  private handleNetworkChange = (): void => {
    console.log('[BandwidthMonitor] Network change detected, re-measuring bandwidth...')
    this.measure()
  }
}

/**
 * Singleton instance for application-wide bandwidth monitoring.
 */
let bandwidthMonitorInstance: BandwidthMonitor | null = null

/**
 * Probe file size in bytes (2 MB)
 */
const PROBE_FILE_SIZE_BYTES = 2 * 1024 * 1024

/**
 * Get the singleton BandwidthMonitor instance.
 * Creates the instance on first call with the uploaded probe file.
 * Uses staging probe URL for development, production for deployed environments.
 */
export function getBandwidthMonitor(): BandwidthMonitor {
  if (!bandwidthMonitorInstance) {
    // Use staging probe for development, production probe for production builds
    const isDevelopment = process.env.NODE_ENV === 'development'

    const probeUrl = isDevelopment
      ? 'https://staging-cdn.martini.film/v0.2/probes/probe-2mb.bin'
      : 'https://cdn.martini.film/v0.2/probes/probe-2mb.bin'

    bandwidthMonitorInstance = new BandwidthMonitor({
      probeUrl,
      probeSizeInBytes: PROBE_FILE_SIZE_BYTES,
      // smoothingAlpha uses DEFAULT (0.3 for moderate smoothing)
      requestTimeoutMs: 10000,
    })
  }
  return bandwidthMonitorInstance
}

/**
 * Initialize bandwidth monitoring.
 * Should be called once during app initialization.
 */
export function initializeBandwidthMonitoring(): void {
  const monitor = getBandwidthMonitor()
  monitor.start()
  console.log('[BandwidthMonitor] Monitoring initialized and started')
}

/**
 * Stop bandwidth monitoring.
 * Should be called during app cleanup.
 */
export function stopBandwidthMonitoring(): void {
  if (bandwidthMonitorInstance) {
    bandwidthMonitorInstance.stop()
    console.log('[BandwidthMonitor] Monitoring stopped')
  }
}
